import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { UserPlus, MailCheck } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { loginWith } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", abha_id: "", age: "", chief_complaints: "" });
  const [consent, setConsent] = useState(false);
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitRegister = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please accept the privacy consent to continue");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, age: form.age ? Number(form.age) : null });
      if (data.demo_otp) {
        setDemoOtp(data.demo_otp);
        toast.info(`Email delivery unavailable — your OTP is ${data.demo_otp}`);
      } else {
        toast.success("OTP sent to your email");
      }
      setStep(1);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async () => {
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: form.email, otp });
      loginWith(data.user);
      if (data.user.unique_id) {
        toast.success(`Account verified! Your Unique Patient ID is ${data.user.unique_id}`, { duration: 8000 });
      } else {
        toast.success("Account verified! Welcome aboard.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-otp", { email: form.email });
      if (data.demo_otp) {
        setDemoOtp(data.demo_otp);
        toast.info(`Email delivery unavailable — your new OTP is ${data.demo_otp}`);
      } else toast.success("New OTP sent");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 md:py-24" data-testid="register-page">
      <div className="bg-white rounded-3xl border border-[#EDF1EF] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
        {step === 0 ? (
          <>
            <span className="w-12 h-12 rounded-2xl bg-[#E8DAB2]/70 flex items-center justify-center mb-5">
              <UserPlus className="w-6 h-6 text-slate-700" />
            </span>
            <h1 className="font-heading text-3xl text-slate-900">Patient Registration</h1>
            <p className="text-sm text-slate-500 mt-1">
              Create your secure patient account. We'll verify your email with an OTP.
            </p>
            <form onSubmit={submitRegister} className="mt-7 space-y-4">
              <div>
                <Label htmlFor="reg-name">Full Name</Label>
                <Input id="reg-name" data-testid="register-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="reg-email">Email ID</Label>
                <Input id="reg-email" data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="reg-phone">Phone Number</Label>
                <Input id="reg-phone" data-testid="register-phone-input" type="tel" required pattern="[0-9+\- ]{10,14}" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="reg-age">Age <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input id="reg-age" data-testid="register-age-input" type="number" min={1} max={120} value={form.age} onChange={set("age")} placeholder="Your age in years" className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="reg-password">Password</Label>
                <Input id="reg-password" data-testid="register-password-input" type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="Minimum 8 characters" className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="reg-abha">ABHA ID <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input id="reg-abha" data-testid="register-abha-input" inputMode="numeric" pattern="[0-9\- ]{14,17}" value={form.abha_id} onChange={set("abha_id")} placeholder="14-digit ABHA number, if you have one" className="mt-1.5 rounded-xl" />
                <p className="text-[11px] text-slate-400 mt-1">
                  Have an Ayushman Bharat Health Account (ABHA)? Add it now or anytime later from your dashboard.
                </p>
              </div>
              <div>
                <Label htmlFor="reg-complaints">Chief Complaints <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea id="reg-complaints" data-testid="register-complaints-input" value={form.chief_complaints} onChange={set("chief_complaints")} placeholder="Briefly describe your main health concern(s)…" className="mt-1.5 rounded-xl" rows={2} />
              </div>
              <label className="flex items-start gap-3 text-xs text-slate-500 leading-relaxed cursor-pointer">
                <Checkbox
                  data-testid="register-consent-checkbox"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  className="mt-0.5"
                />
                <span>
                  I consent to the secure processing of my personal and health information for the
                  purpose of my care, in accordance with the DPDP Act, 2023. My data will be
                  accessible only to me and the doctor's team.
                </span>
              </label>
              <Button
                data-testid="register-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6"
              >
                {loading ? "Sending OTP…" : "Register & Get OTP"}
              </Button>
            </form>
            <p className="text-sm text-slate-500 text-center mt-6">
              Already registered?{" "}
              <Link to="/login" className="text-[#2C557E] font-medium hover:underline">
                Log in
              </Link>
            </p>
          </>
        ) : (
          <>
            <span className="w-12 h-12 rounded-2xl bg-[#D9E4E1] flex items-center justify-center mb-5">
              <MailCheck className="w-6 h-6 text-[#4A7065]" />
            </span>
            <h1 className="font-heading text-3xl text-slate-900">Verify your email</h1>
            <p className="text-sm text-slate-500 mt-1">
              Enter the 6-digit OTP sent to <b>{form.email}</b>
            </p>
            {demoOtp && (
              <div data-testid="register-demo-otp" className="mt-4 bg-[#E8DAB2]/50 border border-[#E8DAB2] rounded-xl p-3 text-sm text-slate-700 text-center">
                Email delivery is unavailable for this address right now — your OTP is{" "}
                <b className="tracking-widest">{demoOtp}</b>
              </div>
            )}
            <div className="mt-6 flex justify-center" data-testid="register-otp-input">
              <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button
              data-testid="register-verify-otp-btn"
              onClick={submitOtp}
              disabled={loading || otp.length !== 6}
              className="w-full mt-6 rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6"
            >
              {loading ? "Verifying…" : "Verify & Create Account"}
            </Button>
            <button
              data-testid="register-resend-otp-btn"
              onClick={resend}
              className="block mx-auto mt-4 text-sm text-[#2C557E] hover:underline"
            >
              Resend OTP
            </button>
          </>
        )}
      </div>
    </div>
  );
}
